import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import api, { apiErr } from "@/lib/api";
import { Satellite, Search, Loader2, Cloud, Leaf, Check, X } from "lucide-react";
import { toast } from "sonner";

const iso = (d) => d.toISOString().slice(0, 10);

export const SatellitePicker = ({ selected, onSelect }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const rectRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(iso(new Date(Date.now() - 90 * 86400000)));
  const [end, setEnd] = useState(iso(new Date()));
  const [maxCloud, setMaxCloud] = useState(20);
  const [scenes, setScenes] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !ref.current || mapRef.current) return;
    const map = L.map(ref.current, { zoomControl: true, attributionControl: true }).setView([37.87, 32.49], 9);
    L.tileLayer("https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2020_3857/default/g/{z}/{y}/{x}.jpg", {
      attribution: "Sentinel-2 cloudless © EOX · Copernicus", maxZoom: 14,
    }).addTo(map);
    const drawRect = () => {
      const b = map.getBounds().pad(-0.25);
      if (rectRef.current) rectRef.current.setBounds(b);
      else rectRef.current = L.rectangle(b, { color: "#00FFFF", weight: 2, fill: false, dashArray: "6 4" }).addTo(map);
    };
    map.on("moveend", drawRect);
    drawRect();
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 150);
    return () => { map.remove(); mapRef.current = null; rectRef.current = null; };
  }, [open]);

  const search = async () => {
    const b = rectRef.current?.getBounds();
    if (!b) return;
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].map((v) => v.toFixed(4)).join(",");
    setLoading(true);
    try {
      const { data } = await api.get("/satellite/search", { params: { bbox, start, end, max_cloud: maxCloud, limit: 12 } });
      setScenes(data.scenes);
      if (!data.scenes.length) toast.info("No scenes found — widen the date range or cloud limit");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="mb-4" data-testid="satellite-picker">
      <button data-testid="satellite-toggle" onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-4 py-3 border transition-colors ${open || selected ? "border-[#00FFFF]/60 bg-[#00FFFF]/5" : "border-white/10 hover:border-white/25"}`}>
        <span className="flex items-center gap-2 text-sm"><Satellite className="h-4 w-4 text-[#00FFFF]" /> Live Sentinel-2 scene</span>
        <span className="text-xs font-mono text-slate-500">{selected ? "SELECTED" : open ? "CLOSE" : "PICK"}</span>
      </button>

      {selected && (
        <div className="flex items-center gap-3 border border-[#00FFFF]/30 bg-[#050A15] p-2 mt-2" data-testid="selected-scene">
          {selected.thumbnail && <img src={selected.thumbnail} alt="scene" className="h-12 w-12 object-cover" />}
          <div className="flex-1 min-w-0 text-xs font-mono">
            <div className="text-[#00FFFF] truncate">{selected.id}</div>
            <div className="text-slate-500">{selected.datetime?.slice(0, 10)} · cloud {selected.cloud_cover}% · veg {selected.vegetation_pct}%</div>
          </div>
          <button data-testid="clear-scene" onClick={() => onSelect(null)} className="text-slate-500 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
      )}

      {open && (
        <div className="mt-2 border border-white/10 p-3 space-y-3 qs-sat">
          <div ref={ref} style={{ height: 220 }} data-testid="satellite-map" />
          <p className="text-[11px] text-slate-500">Pan/zoom so the dashed box covers your field, then search.</p>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="scene-start" type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="bg-[#050A15] border border-white/10 px-2 py-1.5 text-xs font-mono outline-none focus:border-[#00FFFF]" />
            <input data-testid="scene-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="bg-[#050A15] border border-white/10 px-2 py-1.5 text-xs font-mono outline-none focus:border-[#00FFFF]" />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Cloud className="h-3.5 w-3.5 text-slate-500" />
            <input data-testid="scene-cloud" type="range" min={0} max={100} value={maxCloud} onChange={(e) => setMaxCloud(+e.target.value)} className="flex-1 accent-[#00FFFF]" />
            <span className="font-mono text-slate-400 w-10 text-right">≤{maxCloud}%</span>
          </div>
          <button data-testid="scene-search" onClick={search} disabled={loading}
            className="w-full flex items-center justify-center gap-2 border border-[#00FFFF]/50 text-[#00FFFF] py-2 text-sm hover:bg-[#00FFFF]/10 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4" /> Search scenes</>}
          </button>

          {scenes && (
            <div className="space-y-1.5 max-h-72 overflow-auto" data-testid="scene-list">
              {scenes.map((s) => (
                <button key={s.id} data-testid="scene-item" onClick={() => { onSelect(s); setOpen(false); }}
                  className={`w-full flex items-center gap-3 border p-2 text-left transition-colors ${selected?.id === s.id ? "border-[#00FFFF]" : "border-white/10 hover:border-white/30"}`}>
                  <img src={s.thumbnail} alt="" className="h-14 w-14 object-cover bg-[#050A15]" loading="lazy" />
                  <div className="flex-1 min-w-0 text-xs font-mono">
                    <div className="text-slate-200 truncate">{s.datetime?.slice(0, 10)} · {s.platform}</div>
                    <div className="text-slate-500 truncate">{s.tile}</div>
                    <div className="flex gap-3 text-slate-400 mt-0.5">
                      <span className="flex items-center gap-1"><Cloud className="h-3 w-3" />{s.cloud_cover}%</span>
                      <span className="flex items-center gap-1 text-[#00FF7F]"><Leaf className="h-3 w-3" />{s.vegetation_pct}%</span>
                    </div>
                  </div>
                  {selected?.id === s.id && <Check className="h-4 w-4 text-[#00FFFF]" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
